"use client";

/**
 * AdminFilter / AdminFilterField — 검색 필터 공통 컴포넌트 (우선순위 5)
 *
 * 데스크탑 상단 고정 필터 영역.
 * - 필터 필드를 "한 줄에 하나씩" 세로로 배치한다.
 * - 헤더 바를 클릭하면 필터 영역을 접었다/폈다 할 수 있다. (collapsible)
 * - 각 필드는 왼쪽 라벨 + 오른쪽 컨트롤(children) 구조다. (모바일에선 위/아래 스택)
 *
 * 도메인 비종속: 어떤 필터 필드를 둘지는 children 으로 주입하고,
 * 검색/초기화 동작은 콜백(onSearch/onReset)으로 외부에서 처리한다.
 * (form submit 으로 동작하므로 입력창에서 Enter 시에도 onSearch 가 호출된다.)
 *
 * @example
 * <AdminFilter title="검색 필터" onSearch={handleSearch} onReset={handleReset}>
 *   <AdminFilterField label="이름" htmlFor="name">
 *     <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
 *   </AdminFilterField>
 *   <AdminFilterField label="상태" htmlFor="status">
 *     <select id="status" ...>...</select>
 *   </AdminFilterField>
 * </AdminFilter>
 */
import { type ReactNode, useState } from "react";
import { ChevronDown, RotateCcw, Search } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Label } from "../ui/label";

export interface AdminFilterProps {
  /** 필터 입력 필드들 (AdminFilterField 들을 넣는다) */
  children: ReactNode;
  /** 검색(submit) 콜백 */
  onSearch?: () => void;
  /** 초기화 콜백. 주어지면 초기화 버튼이 표시된다 */
  onReset?: () => void;
  /** 필터 헤더 라벨 (기본 "검색 필터") */
  title?: ReactNode;
  /** 접기/펴기 사용 여부 (기본 true) */
  collapsible?: boolean;
  /** 초기 펼침 상태 (기본 true) */
  defaultOpen?: boolean;
  /** 검색 버튼 라벨 (기본 "검색") */
  searchLabel?: string;
  /** 초기화 버튼 라벨 (기본 "초기화") */
  resetLabel?: string;
  /** 검색 중 비활성화/로딩 표시 */
  loading?: boolean;
  /** 버튼 영역에 추가로 둘 액션 */
  actions?: ReactNode;
  className?: string;
}

export function AdminFilter({
  children,
  onSearch,
  onReset,
  title = "검색 필터",
  collapsible = true,
  defaultOpen = true,
  searchLabel = "검색",
  resetLabel = "초기화",
  loading = false,
  actions,
  className,
}: AdminFilterProps) {
  const [open, setOpen] = useState(defaultOpen);
  const expanded = collapsible ? open : true;

  return (
    <div
      className={cn(
        "rounded-lg border bg-card text-card-foreground shadow-xs",
        className
      )}
    >
      {/* 헤더 (접기/펴기 토글) */}
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-between rounded-t-lg px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-accent/50"
        >
          {title}
          <ChevronDown
            className={cn(
              "size-4 shrink-0 text-muted-foreground transition-transform",
              !expanded && "-rotate-90"
            )}
          />
        </button>
      ) : (
        title && (
          <div className="px-4 py-3 text-sm font-medium">{title}</div>
        )
      )}

      {expanded && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSearch?.();
          }}
        >
          {/* 필드: 한 줄에 하나씩 (필드 간 구분선 없이 간격으로 구분) */}
          <div className="space-y-3 border-t px-4 py-4">{children}</div>

          {/* 버튼 영역 */}
          <div className="flex items-center justify-end gap-2 px-4 pb-4">
            {actions}
            {onReset && (
              <Button
                type="button"
                variant="outline"
                onClick={onReset}
                disabled={loading}
              >
                <RotateCcw className="size-4" />
                {resetLabel}
              </Button>
            )}
            <Button type="submit" disabled={loading}>
              <Search className="size-4" />
              {searchLabel}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export interface AdminFilterFieldProps {
  /** 필드 라벨 (왼쪽) */
  label?: ReactNode;
  /** 라벨과 연결할 입력 요소 id */
  htmlFor?: string;
  /** 컨트롤(오른쪽) */
  children: ReactNode;
  /** 라벨 영역 너비 클래스 (기본 sm:w-32) */
  labelClassName?: string;
  className?: string;
}

export function AdminFilterField({
  label,
  htmlFor,
  children,
  labelClassName,
  className,
}: AdminFilterFieldProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4",
        className
      )}
    >
      {label && (
        <Label
          htmlFor={htmlFor}
          className={cn(
            "shrink-0 text-muted-foreground sm:w-32",
            labelClassName
          )}
        >
          {label}
        </Label>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
